import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UpdateOrderStatusDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (status: 'new' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded', notes?: string) => void | Promise<void>;
  currentStatus: 'new' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  isLoading?: boolean;
}

export function UpdateOrderStatusDialog({
  isOpen,
  onClose,
  onConfirm,
  currentStatus,
  isLoading = false,
}: UpdateOrderStatusDialogProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'new' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded'>(
    currentStatus
  );
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    void onConfirm(status, notes || undefined);
    setNotes('');
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
      setNotes('');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-neutral-dark/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6 md:p-8 max-w-md w-full animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full text-brand-accent bg-brand-accent/20">
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </div>

        {/* Title */}
        <h3 className="text-2xl font-heading font-bold text-brand-accent mb-6 text-center">
          {t('admin.orders.updateStatus.title') || 'Update Order Status'}
        </h3>

        {/* Form */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.orders.updateStatus.selectStatus') || 'Select Status'}
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="new">{t('admin.orders.status.new') || 'New'}</option>
              <option value="processing">{t('admin.orders.status.processing') || 'Processing'}</option>
              <option value="completed">{t('admin.orders.status.completed') || 'Completed'}</option>
              <option value="failed">{t('admin.orders.status.failed') || 'Failed'}</option>
              <option value="cancelled">{t('admin.orders.status.cancelled') || 'Cancelled'}</option>
              <option value="refunded">{t('admin.orders.status.refunded') || 'Refunded'}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.orders.updateStatus.notes') || 'Notes (Optional)'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('admin.orders.updateStatus.notesPlaceholder') || 'Enter notes...'}
              rows={3}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => {
              onClose();
              setNotes('');
            }}
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/80 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? t('common.loading') || 'Loading...' : t('common.confirm') || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

